import { Grid, IconButton, Tooltip,} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { Button, CollapsibleSection,  MainCard, MuiTable, StatusChip, TextValidator, ValidatorForm, ConfirmDialog, TableTopSearch } from 'components';
import { OrganizationDropDown/*, DesignationDropDown*/ } from 'AutoComplites';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { scrollToTop } from 'Utils';
import EmployeesServices from 'services/EmployeesServices';
import localStorageService from 'services/localStorageService';

const initial_form = {
    organization: null,
    admin_name: null,
    email: null,
    phone: null,
    designation: null,
    status: "Active",
    userId: null,
    "order[0]": ["updatedAt", "DESC"],
}

const AddOGrgSetup = () => {
    const [formData, setFormData] = useState(initial_form);
    const [orgAdmins, setOrgAdmins] = useState([]);
    
    const updateAdminStatus = async (id, status) => {
        const user = await localStorageService.getItem("user");
        if (user && user.id === id) {
            toast.error("Cannot change status of the currently logged-in user.");
            return;
        }
        setLoading(true);
        try {
            const updatedStatus = status === "Active" ? "Inactive" : "Active";
            const response = await EmployeesServices.updateStatus(id, {status:updatedStatus});
            if (response && response.success) {
                toast.success( `Status changed to ${updatedStatus}`);
                getData();
            } else {
                toast.error( 'Failed to update status.');
            }
        } catch (error) {
            console.error('Status update error:', error);
            toast.error(`An unexpected error occurred: ${error?.message || error}`);
        } finally {
            setLoading(false);
            getData()
        }
    };

    
    const handleChangeStatus = async (id, status) => {
        const actionText = status === "Active"
            ? "deactivate this admin"
            : "activate this admin";
        ConfirmDialog({
            type: status === "Active" ? 'error' : 'success',
            title: `Are you sure you want to ${actionText}?`,
            description:
                status === "Active"
                    ? "This will deactivate the admin. You can reactivate it later."
                    : "This will activate the admin. You can deactivate it later.",
            buttons: [
                {
                    label: 'No',
                    onClick: () => toast.info('Action cancelled.')
                },
                {
                    label: 'Yes',
                    onClick: async () => {
                        await updateAdminStatus(id, status);
                    }
                }
            ]
        });
    };
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [filters, setFilters] = useState({
        page: 0,
        limit: 10,
        'order[0]': ['updatedAt', 'DESC'],
        search: null,
    });
    const [totalItems, setTotalItems] = useState(0);

    const getData = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = {
                page: filters.page,
                limit: filters.limit,
                'order[0]': filters['order[0]'],
                type: 'Organization Admin',
            };
            if (filters.search) {
                queryParams.search = filters.search;
            }
            if (filters.organization_id) {
                queryParams.organization_id = filters.organization_id;
            }
            if (filters.designation_id) {
                queryParams.designation_id = filters.designation_id;
            }

            const response = await EmployeesServices.get(queryParams);
            if (response.success) {
                const responseData = response?.data?.view?.data || response?.data || [];
                setData(responseData);
                setTotalItems(response?.data?.view?.totalItems || responseData.length);
            }
        } catch (error) {
            toast.error('Failed to fetch data. Please try again.');
            setData([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        getData();
    }, [getData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

   
    const fetchOrgAdmins = async (orgId) => {
        if (!orgId) {
            setOrgAdmins([]);
            return;
        }
        setLoading(true);
        try {
            const queryParams = {
                organization_id: orgId,
                type: 'Organization Admin',
                limit: 100,
                page: 0
            };
            const response = await EmployeesServices.get(queryParams);
            if (response.success) {
                const responseData = response?.data?.view?.data || response?.data || [];
                setOrgAdmins(responseData);
            } else {
                setOrgAdmins([]);
            }
        } catch (error) {
            setOrgAdmins([]);
        } finally {
            setLoading(false);
        }
    };

    const checkEmailExists = async (email, excludeId = null) => {
        try {
            const queryParams = {
                search: email,
                type: 'Organization Admin',
                limit: 100,
                page: 0
            };
            const response = await EmployeesServices.get(queryParams);
            if (response.success) {
                const responseData = response?.data?.view?.data || response?.data || [];
                const existingEmail = responseData.find(admin => 
                    admin.email && admin.email.toLowerCase() === email.toLowerCase() && 
                    (!excludeId || admin.id !== excludeId)
                );
                return !!existingEmail;
            }
            return false;
        } catch (error) {
            console.error('Error checking email existence:', error);
            return false;
        }
    };

    const setPage = (page) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            page: page - 1,
        }));
    };

    const setPageSize = (pageSize) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            page: 0,
            limit: pageSize,
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (!formData.organization) {
                toast.error('Organization is required!');
                setSubmitting(false);
                return;
            }
            if (formData.email) {
                const emailExists = await checkEmailExists(formData.email, isEdit ? editingData : null);
                if (emailExists) {
                    toast.error('This email already exists. Please use a different email address.');
                    setSubmitting(false);
                    return;
                }
            }

            const apiData = Object.fromEntries(
                Object.entries(formData).filter(([_, value]) => value !== null && value !== '')
            );

    
            apiData.type = 'Organization Admin';
            if (!apiData.status) {
                apiData.status = 'Active';
            }

            if (apiData.admin_name) {
                apiData.name = apiData.admin_name;
                delete apiData.admin_name;
            }

            if (apiData.phone) {
                apiData.contact_no = apiData.phone;
                delete apiData.phone;
            }

            if (apiData.organization) {
                apiData.organization_id = apiData.organization;
                delete apiData.organization;
            }
            // if (apiData.designation) {
            //     apiData.designation_id = apiData.designation;
            //     delete apiData.designation;
            // }

            let response;
            if (isEdit) {
                response = await EmployeesServices.update(editingData, apiData);
                if (response.success) {
                    toast.success( 'Admin updated successfully!');
                } else {
                    toast.error( 'Failed to update. Please try again.');
                }
            } else {
                response = await EmployeesServices.create(apiData);
                if (response.success) {
                    toast.success( 'Admin created successfully!');
                } else {
                    toast.error( 'Failed to create. Please try again.');
                }
            }

            if (response.success) {
                setFormData(initial_form);
                setIsEdit(false);
                setOpenEditModal(false);
                setEditingData(null);
                getData();
                scrollToTop();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('An unexpected error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <MainCard>
                <CollapsibleSection title="All admins" collapsTrigger={openEditModal} buttonText='Add an admin'>
                    <ValidatorForm onSubmit={handleSubmit} process={submitting}>
                        <Grid container spacing={1}>
                            <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                                <OrganizationDropDown
                                    name="organization"
                                    label="Organization"
                                    value={formData.organization || ''}
                                    onChange={async (event, newValue) => {
                                        const orgId = newValue ? newValue.id : '';
                                        setFormData(prev => ({
                                            ...prev,
                                            organization: orgId
                                        }));
                                        await fetchOrgAdmins(orgId);
                                    }}
                                    required={true}
                                    validators={["required"]}
                                    errorMessages={["This field is required"]}
                                    disabled={loading}
                                    filter={{ status: 'Active' }}
                                   
                                />
                            </Grid>
                            <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                                <TextValidator
                                    label="Admin Name"
                                    name="admin_name"
                                    value={formData.admin_name || ''}
                                    onChange={handleInputChange}
                                    validators={['required']}
                                    errorMessages={['This field is required']}
                                    required={true}
                                    disabled={loading}
                                    placeholder="Enter admin name"
                                />
                            </Grid>
                            <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                                <TextValidator
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email || ''}
                                    onChange={handleInputChange}
                                    validators={['required', 'isEmail']}
                                    errorMessages={['This field is required', 'Email is not valid']}
                                    required={true}
                                    disabled={loading}
                                    placeholder="Enter email address"
                                />
                            </Grid>
                            <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                                <TextValidator
                                    label="Phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone || ''}
                                    onChange={handleInputChange}
                                    validators={['required', 'matchRegexp:^\\d+$', 'maxStringLength:10']}
                                    errorMessages={['This field is required', 'Only numbers are allowed', 'Maximum length is 10 digits']}
                                    required={true}
                                    disabled={loading}
                                    placeholder="Enter phone number"
                                    inputProps={{ maxLength: 10 }}
                                />
                            </Grid>
                            {/* <Grid size={{ lg: 3, md: 4, sm: 6, xs: 12 }}>
                                <DesignationDropDown
                                    name="designation"
                                    label="Designation"
                                    value={formData.designation || ''}
                                    onChange={(event, newValue) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            designation: newValue ? newValue.id : ''
                                        }));
                                    }}
                                    required={true}
                                    validators={["required"]}
                                    errorMessages={["This field is required"]}
                                    disabled={loading}
                                />
                            </Grid> */}
                        </Grid>

                        <Grid className="mt-2" container spacing={1}>
                            <Grid>
                                <Button type="submit" process={submitting}>
                                    {isEdit ? "UPDATE" : "SAVE"}
                                </Button>
                            </Grid>
                            <Grid>
                                {openEditModal && (
                                    <Button
                                        className="btn-warning"
                                        onClick={() => {
                                            ConfirmDialog({
                                                type: "error",
                                                title: "Are you sure you want to cancel?",
                                                description: "This will discard all unsaved changes. You cannot undo this action.",
                                                buttons: [
                                                    {
                                                        label: "No",
                                                        onClick: () => toast.info("Action cancelled."),
                                                    },
                                                    {
                                                        label: "Yes",
                                                        onClick: () => {
                                                            setFormData(initial_form);
                                                            setIsEdit(false);
                                                            setOpenEditModal(false);
                                                            setEditingData(null);
                                                            toast.success("Form cancelled successfully.");
                                                        },
                                                    },
                                                ],
                                            });
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </ValidatorForm>
                </CollapsibleSection>
                
                <div>
                   
                          <ValidatorForm style={{ marginBottom: 24 }}>
                              <Grid container spacing={1} alignItems="center">
                            <Grid item size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
                                <OrganizationDropDown
                                    name="filter_organization"
                                    label="Organization"
                                    value={filters.organization_id || ''}
                                    onChange={(event, newValue) => {
                                        setFilters(prevData => ({
                                            ...prevData,
                                            organization_id: newValue ? newValue.id : ''
                                        }));
                                    }}
                                    required={false}
                                    disabled={loading}
                                    filter={{ status: 'Active' }}
                                />
                            </Grid>
                            {/* <Grid item size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
                                <DesignationDropDown
                                    name="filter_designation"
                                    label="Designation"
                                    value={filters.designation_id || ''}
                                    onChange={(event, newValue) => {
                                        setFilters(prevData => ({
                                            ...prevData,
                                            designation_id: newValue ? newValue.id : ''
                                        }));
                                    }}
                                    required={false}
                                    disabled={loading}
                                />
                            </Grid> */}

                            <Grid className="mt-5">
                                <Button 
                                    variant="outlined" color="error"
                                    onClick={() => {
                                        setFilters(prevFilters => ({
                                            ...prevFilters,
                                            organization_id: '',
                                            designation_id: '',
                                            page: 0,
                                            search: ''
                                        }));
                                    }}
                                    sx={{ height: 40, minWidth: 90, boxSizing: 'border-box' }}
                                >
                                    <FilterAltOutlinedIcon sx={{ mr: 1 }} />
                                    Clear
                                </Button>
                            </Grid>

                        </Grid>
                    </ValidatorForm>

                    <TableTopSearch
                        title='Admin'
                        showSearch={true}
                        showTotalWithTitle={true}
                        rowCount={totalItems}
                        onSubmitSearch={null}
                        onSearchChange={(e) => {
                            setFilters(prevData => ({
                                ...prevData,
                                search: e.target.value
                            }));
                        }}
                    />

                    <MuiTable
                        columns={[
                            {
                                accessorKey: 'name',
                                header: 'Name',
                                Cell: ({ row }) => <span>{row.original.name || ''}</span>
                            },
                            {
                                accessorKey: 'organization',
                                header: 'Organization',
                                Cell: ({ row }) => <span>{row.original.organizationDetails?.name || ''}</span>
                            },
                            {
                                accessorKey: 'designation',
                                header: 'Designation',
                                Cell: ({ row }) => <span>{row.original.designationDetails?.name || ''}</span>
                            },
                            {
                                accessorKey: 'contact_no',
                                header: 'Phone',
                                Cell: ({ row }) => <span>{row.original.contact_no || row.original.phone || ''}</span>
                            },
                            {
                                accessorKey: 'email',
                                header: 'Email',
                                Cell: ({ row }) => <span>{row.original.email || ''}</span>
                            },
                            {
                                accessorKey: 'type',
                                header: 'Type',
                                Cell: ({ row }) => <span>{row.original.type || ''}</span>
                            },
                            {
                                accessorKey: 'createdAt',
                                header: 'Created Date',
                                Cell: ({ row }) => <span>{row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : ''}</span>
                            },
                            {
                                accessorKey: 'createdAtTime',
                                header: 'Created Time',
                                Cell: ({ row }) => <span>{row.original.createdAt ? new Date(row.original.createdAt).toLocaleTimeString() : ''}</span>
                            },
                            {
                                accessorKey: 'status',
                                header: 'Status',
                                Cell: ({ cell }) => (
                                    <div>
                                        <StatusChip 
                                            status={cell.row.original.status} 
                                            onClick={() => { handleChangeStatus(cell.row.original.id, cell.row.original.status) }}
                                        />
                                    </div>
                                ),
                                headerProps: { style: { textAlign: 'right' } },
                                cellProps: { style: { textAlign: 'right' } },
                            },
                            {
                                accessorKey: 'actions',
                                header: 'Actions',
                                Cell: ({ cell }) => (
                                    <div>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" color="secondary" onClick={() => {
                                                setIsEdit(false);
                                                setOpenEditModal(false);
                                                setTimeout(() => {
                                                    const editData = {
                                                        ...cell.row.original,
                                                        admin_name: cell.row.original.name,
                                                        phone: cell.row.original.contact_no,
                                                        organization: cell.row.original.organization_id,
                                                        designation: cell.row.original.designation_id
                                                    };
                                                    setFormData(editData);
                                                    setIsEdit(true);
                                                    setOpenEditModal(true);
                                                    setEditingData(cell.row.original.id);
                                                    scrollToTop();
                                                }, 500);
                                            }}>
                                                <DriveFileRenameOutlineOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                ),
                                headerProps: { style: { textAlign: 'right' } },
                                cellProps: { style: { textAlign: 'right' } },
                            },
                        ]}
                        data={data}
                        title="All Employees"
                        manualPagination={true}
                        rowCount={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        loading={loading}
                    />
                </div>
            </MainCard>
        </div>
    );
};

export default AddOGrgSetup;
